import { useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useSituation } from "../panel/situation";
import { InPlace } from "../panel/InPlace";
import { badgeState, readAnswer } from "../badge/badge";
import { sameSession } from "../auth/session";
import { useSessionKey } from "../auth/useSessionKey";
import { useEvent } from "../context/EventProvider";
import { useAccessLog } from "../log/actionLog";
import { logParticipation, stepsOf } from "../log/participation";
import { isRegistered, PartialWriteError } from "../funnel/participant";
import { pageOf, pagePath } from "../pages/registry";
import { isClosed } from "../pages/closedPages";
import { hasAnswered, useAnswers, type Answers } from "../funnel/answers";
import { Checkout } from "../funnel/Checkout";
import { InvoiceList } from "../funnel/InvoiceList";
import { Recap } from "../funnel/Recap";
import { firstStep } from "../funnel/thread";
import { useResume } from "../funnel/useArticles";
import { useOffer } from "../funnel/useOffre";
import { useParticipation } from "../funnel/useParticipation";
import { useMode } from "../panel/mode";
import { fallbackOf } from "../settings/catalog";
import { useFlagOr, useSettings, useT, useText } from "../settings/context";
import { Button } from "../ui/Button";
import { Prose } from "../ui/Prose";
import { withContext } from "./registry";
import { sendConfirmation } from "../emails/confirmation";

/**
 * REGISTRATION, LAST SCREEN — where the answers are RECORDED.
 *
 * ⚠️ IT DOES NOT PROMISE BEFORE IT HAS WRITTEN. The screen says "your registration
 * is recorded"; showing that while the request is still out, or after it failed, is
 * the one lie this screen must not tell — a participant who reads it stops there,
 * and nobody finds out until the door. So the configured wording AND the recap wait
 * for the write, and a failure is said plainly with a way to try again.
 *
 * The three states are told apart by their WORDS, not by a symbol: the green ring
 * the prototype draws here was removed on request. Nothing is lost — it only ever
 * repeated what the heading already said.
 *
 * ⚠️ IT ONLY WRITES WHEN SOMETHING WAS ANSWERED. An organiser reaching this screen
 * from the panel's screen picker carries no answers — writing then would record
 * THEIR own participation in the event they are configuring.
 *
 * ⚠️ THE WORDING FOLLOWS THE ANSWER. Thanking somebody for coming when they have
 * just declined is the mistake this screen exists to make impossible, and a single
 * configurable sentence cannot avoid it: whoever writes it picks a case and is
 * wrong in the other. Attending remotely gets its own line for the same reason.
 *
 * ⚠️ WHAT THE PROTOTYPE ALSO PUTS HERE, AND WHY IT IS NOT HERE YET. Each of these
 * needs a screen or a service that does not exist, and the catalog's own rule is
 * that a control which cannot work is not drawn:
 *
 *   QR code            needs the badge code AND an encoder. A QR that does not
 *                      encode the real code is worse than none — it scans, and
 *                      lets somebody through on nothing. ⚠️ THAT GAP IS NOW FILLED,
 *                      but ELSEWHERE: the badge screen carries the QR and the
 *                      download, and this screen LEADS to it (see below).
 *                      Duplicating it here would mean two places to keep in
 *                      agreement.
 *   PDF recap          needs a generator.
 *   "Invite somebody"  needs `create-invite`, which sends them a badge.
 *
 * ⚠️ LE PAIEMENT, LUI, EST ICI — et il ne pouvait être nulle part ailleurs. Le serveur
 * facture ce qui est ENREGISTRÉ, or la commande ne l'est qu'à cet écran : c'est la
 * raison technique qui l'y place, pas une préférence de mise en page. Voir
 * `funnel/payment.ts`. Le troisième état de la confirmation — « il reste une étape » —
 * est donc porté par le bloc lui-même, qui se tait quand il n'y a rien à régler.
 *
 * ⚠️ LES FACTURES AUSSI, ET ELLES ONT FAILLI NE PAS Y ÊTRE. La route de téléchargement
 * d'api-v1 exige un `Bills` nommé par `FO_MEETING_ARTICLES_BILL_ID`, absent de
 * l'événement de test — j'en avais conclu trop vite que le document n'était pas
 * livrable. C'est la SECONDE route, celle d'apix, qui sert ici : elle n'exige aucun
 * réglage. Voir `funnel/invoices.ts`, où les deux sont départagées.
 *
 * The recap itself only reports what V2 collects today; conferences, companions and
 * amounts join it as their steps land.
 *
 * Displayed strings go through `t()`, French kept at the call site as the
 * fallback: what the site says of itself follows the language being read.
 */

/** What the organiser wrote, or what the catalog promises in its place. */
function useLine(key: string): string {
  return useText(key, fallbackOf(key));
}

/**
 * ═══ LES ENTRÉES D'HISTORIQUE ATTEINTES EN MODE ORGANISATEUR (tâche #195) ═══
 *
 * Signalé : un email « réponse négative » reçu sans refus donné en participant. La
 * garde `mode !== "visitor"` ne se lisait qu'à l'instant : l'écriture
 * et l'email suivent `mode`, donc un organisateur qui parcourt son tunnel pour le
 * régler (« Je ne participe pas », pour voir cet écran) puis repasse en vue visiteur
 * sur la confirmation voyait partir son parcours de réglage, écrit pour de vrai et
 * suivi de l'email de l'action. Trois façons de repasser en visiteur : « Voir le
 * site », le droit qui tombe (`Shell.tsx`), et le site remonté derrière l'écran de
 * chargement, qui rend un composant neuf au même endroit de l'historique.
 *
 * ⚠️ UNE MARQUE PAR ENTRÉE, ET NON UN `useRef`, à cause de ce troisième cas : un
 * composant remonté repart d'une référence vierge. Même principe que `written` dans
 * `funnel/leaveGuard.ts`.
 *
 * ⚠️ CE QUE L'ON JETTE, CE SONT LES RÉPONSES, pas seulement l'écriture : vu en
 * visiteur, l'écran annoncerait sinon « Merci de nous avoir prévenus » d'un refus que
 * personne n'a enregistré. Il relit ce que le serveur détient, comme depuis « Mon
 * inscription ».
 */
const previewed = new Set<string>();

export function Confirmation() {
  const t = useT();
  const [walked] = useAnswers();
  /* ⚠️ RELUE À CHAQUE ARRIVÉE (participation supprimée, 2026-10-20) : voir l'effet d'écriture. */
  const { save, loading, unavailable, recorded, me, lastWriteChanged, revalidating } =
    useParticipation({ fresh: true });
  const mode = useMode();
  const { value, typos } = useSettings();
  const { key: entry } = useLocation();
  /* La session sous laquelle cet écran a composé ses réponses : voir `auth/session.ts`. */
  const sessionKey = useSessionKey();
  const walkedSomething = hasAnswered(walked);
  useEffect(() => {
    if (mode !== "visitor" && walkedSomething) previewed.add(entry);
  }, [mode, walkedSomething, entry]);
  const answers: Answers =
    mode === "visitor" && previewed.has(entry) ? {} : walked;

  /**
   * ═══ THE ANSWER THAT COUNTS: THE ONE JUST GIVEN, ELSE THE ONE ON THE SERVER ═══
   *
   * ⚠️ THE SECOND HALF WAS MISSING, AND THIS SCREEN EXISTS TO AVOID EXACTLY THAT. The
   * wording was read off the navigation state alone, which is empty for anybody who
   * merely OPENS their recap — from their account menu, from a link, after a reload. So
   * somebody who had declined was told "Votre inscription est enregistrée", the one
   * sentence this screen must never say to them. Measured in the browser on a declining
   * account before it was fixed.
   */
  /**
   * ⚠️ EN MODE ORGANISATEUR, LA SITUATION CHOISIE DANS LE PANNEAU PRIME sur la réponse
   * enregistrée (celle de l'organisateur lui-même, sans intérêt ici) : c'est ce qui lui
   * montre les cinq versions de l'écran. Aucune écriture ne dépend de ce choix — elles
   * n'ont lieu qu'en mode visiteur, où la situation vaut `null`. Voir
   * `panel/situation.tsx`.
   */
  const forced = useSituation();
  const answer = forced
    ? forced === "pending"
      ? "onsite"
      : forced
    : readAnswer(answers.participation ?? recorded?.answer);
  const declined = answer === "decline";
  const remote = answer === "remote";
  /**
   * ═══ CE QUI DÉCLENCHE L'ÉCRITURE : DES RÉPONSES À ÉCRIRE ═══
   *
   * On n'arrive ici qu'au bout du parcours ou en y revenant, et dans les deux cas ce sont
   * les réponses portées par l'état de navigation qui sont enregistrées. Rien à écrire,
   * rien n'est écrit — c'est ce qui protège l'ORGANISATEUR, qui ouvre cet écran depuis le
   * sélecteur du panneau pour le configurer : sans réponse en main, sa propre
   * participation à l'événement qu'il prépare n'est pas enregistrée.
   *
   * ⚠️ ET C'EST LA GARDE DE NAVIGATION QUI REND CELA SÛR, pas une vérification d'ici. Qui
   * n'a pas répondu ne peut pas atteindre cet écran : le fil d'Ariane ne mène nulle part
   * tant que `responded` est faux, et une adresse ouverte à froid renvoie à la première
   * étape (voir `Shell.tsx`). Cet écran n'a donc plus à se demander d'où l'on vient — ce
   * qu'il faisait en relisant une liste d'étapes franchies qui survivait aux
   * rechargements, et qui l'a fait inscrire des comptes n'ayant jamais confirmé.
   *
   * ⚠️ ET CE N'EST SÛR QUE PARCE QUE L'ÉCRITURE RETOMBE SUR L'ENREGISTRÉ. Sans réponse de
   * participation dans l'état, `saveParticipation` lisait « ne vient pas » et aurait
   * désinscrit la personne — voir `funnel/participant.ts`, corrigé d'abord.
   */
  const answered = hasAnswered(answers);
  const comingTitle = useLine("FO_MEETING_V2_CONFIRMATION_TITLE");
  const comingMessage = useLine("FO_MEETING_V2_CONFIRMATION_MESSAGE");
  const remoteMessage = useText("FO_MEETING_V2_CONFIRMATION_MESSAGE_REMOTE");
  const html = useText("FO_MEETING_V2_CONFIRMATION_HTML");
  const pendingTitle = useLine("FO_MEETING_V2_CONFIRMATION_PENDING_TITLE");
  const pendingMessage = useLine("FO_MEETING_V2_CONFIRMATION_PENDING_MESSAGE");
  const declineTitle = useLine("FO_MEETING_V2_CONFIRMATION_DECLINE_TITLE");
  const declineMessage = useLine("FO_MEETING_V2_CONFIRMATION_DECLINE_MESSAGE");

  const recapShown = useFlagOr("FO_MEETING_V2_CONFIRMATION_RECAP");
  const editShown = useFlagOr("FO_MEETING_V2_CONFIRMATION_EDIT");
  const backShown = useFlagOr("FO_MEETING_V2_CONFIRMATION_BACK_SHOW");
  const payShown = useFlagOr("FO_MEETING_V2_PAY_SHOW");
  const invoicesShown = useFlagOr("FO_MEETING_V2_INVOICES_SHOW");
  const backLabel = useLine("FO_MEETING_V2_CONFIRMATION_BACK_LABEL");

  const navigate = useNavigate();
  const event = useEvent();

  /**
   * NOTHING TO CONFIRM — so nothing is claimed.
   *
   * Atteint sans avoir parcouru le tunnel et sans rien d'enregistré côté serveur, cet
   * écran annonçait « votre inscription est enregistrée » au-dessus d'un récapitulatif
   * qui ne portait qu'un nom. Mesuré, et c'est le pire qu'il puisse faire : quelqu'un
   * le lit et s'arrête là, et personne ne s'en aperçoit avant la porte.
   *
   * ⚠️ NOT IN ORGANISER MODE. An organiser opens this very screen from the panel's
   * screen picker in order to write what it says; bouncing them would make it the
   * one screen they cannot configure.
   *
   * ⚠️ ONLY ONCE WE KNOW. While the answer is still out, `recorded` is undefined and
   * so is the conclusion — redirecting then would throw people out of a screen they
   * had every right to see. When the identity cannot be resolved at all, the answer
   * is also "we cannot confirm it", and that is a redirect too: sending somebody to
   * the first step costs them a click, claiming a place they do not have costs them
   * the trip.
   *
   * ⚠️ ET « ARRIVER AU BOUT » COMPTE AUTANT QUE `responded`. Les réponses en main sont
   * celles qu'on vient de donner et qui n'ont pas encore été écrites : sans ce terme,
   * cet écran renverrait à la première étape la personne qui vient précisément de
   * terminer son parcours — un aller-retour, et la confirmation jamais lue.
   *
   * ⚠️ LE CHÂSSIS PORTE LA MÊME RÈGLE (`nothingToConfirm` dans `Shell.tsx`), et c'est lui
   * qui redirige d'abord : seul ici, ce renvoi perdait la course contre la note de `loin`
   * du châssis, et « Mon inscription » ouvert depuis une étape laissait un écran vide.
   * Celui-ci reste en filet. Changer l'une, c'est changer l'autre.
   */
  const lost =
    mode === "visitor" && !loading && !answered && recorded?.responded !== true;
  const start = firstStep(value, useOffer());
  // Pas pour qui n'a rien à lire ici et repart à la première étape.
  useAccessLog("confirmationAccess", !(lost && start));

  /**
   * WHERE THE WRITE STANDS, held HERE rather than read off the mutation.
   *
   * Measured: the mutation ran and settled — `onSuccess` then `onSettled` both
   * fired, the row was written — yet the component never re-rendered with
   * `isPending` back to false, so the screen stayed on "recording your answer"
   * for good. Whatever the cause, a screen whose honesty depends on that flag
   * reaching it is a screen that can silently lie. An explicit state cannot.
   */
  const [wrote, setWrote] = useState<"waiting" | "done" | "failed">("waiting");
  /**
   * ═══ CE QUI EST RÉELLEMENT PARTI AVANT L'ÉCHEC ═══
   *
   * ⚠️ L'ÉCRITURE EST EN TROIS APPELS, PAS UNE TRANSACTION. Jours en 200 puis la participation en
   * 500 : l'organisateur a vos jours, et l'écran affirmait « Rien n'a été transmis ». Vide, rien
   * n'est parti et c'est vrai ; sinon le texte dit exactement ce qui l'est. Voir
   * `PartialWriteError`.
   */
  const [partial, setPartial] = useState<{ days: boolean; tickets: boolean } | null>(null);
  /** Une écriture en vol — à nous, plutôt que `save.isPending`, qui ne redescend pas toujours (voir ci-dessus). */
  const writing = useRef(false);
  const write = () => {
    if (writing.current) return;
    writing.current = true;
    setWrote("waiting");
    setPartial(null);
    save
      .mutateAsync(answers)
      .then(() => {
        setWrote("done");
        logWrite();
      })
      .catch((error: unknown) => {
        setPartial(error instanceof PartialWriteError ? error.sent : null);
        setWrote("failed");
      })
      .finally(() => {
        writing.current = false;
      });
  };

  /**
   * Fired ONCE per arrival. Without the guard, each re-render would send the write
   * again — the classic loop that turns one registration into a dozen.
   *
   * ⚠️ AND NEVER IN ORGANISER MODE. The note at the top of this file has always said
   * that an organiser opening this screen from the picker must not record their own
   * participation — but it RESTED on an assumption, "they carry no answers". That is
   * false as soon as they walk the funnel to configure it: they answer, like anybody
   * else, and the screen wrote. The guard now says what the sentence promised.
   */
  const sent = useRef(false);
  /**
   * ═══ ET LE JOURNAL DE LA FICHE CONTACT ═══
   *
   * Inscrit, désinscrit, ou modifié : `log/participation.ts`. Ce qui était enregistré se
   * lit AVANT la première tentative, et un nouvel essai garde cette lecture. Seulement si
   * l'écriture a changé quelque chose chez le serveur : un rechargement, ou un passage sans
   * rien changer, ne s'inscrit pas (bug du journal, chasse du 2026-10-20).
   */
  const registeredBefore = useRef(false);
  const logWrite = () => {
    if (!event) return;
    /* ⚠️ « Y A-T-IL EU CHANGEMENT ? » SE DEMANDE AU SERVEUR, par l'écriture elle-même :
       voir `participationChanged` dans `funnel/participant.ts`. */
    logParticipation(event, () => {
      const typoId = answers.typoId ?? recorded?.typoId;
      return {
        wasRegistered: registeredBefore.current,
        answer,
        typo: typos.find((t) => t.id === typoId)?.name,
        steps: stepsOf(answers),
        // Rien de changé chez le serveur, rien au journal (`participationChanged`).
        changed: lastWriteChanged?.(),
      };
    });
  };
  /**
   * ⚠️ ET JAMAIS SUR UNE PARTICIPATION QUI N'A PAS ÉTÉ RELUE (participation supprimée depuis
   * la fiche contact du BO, 2026-10-20). Mesuré : deux retours arrière après la suppression
   * ramenaient sur cette entrée, ses réponses en main, et l'écran RÉÉCRIVAIT toute
   * l'inscription (`PUT days`, `PUT articles`, `PUT users`) — la suppression était annulée
   * sans un clic. La participation est donc relue à chaque arrivée (`fresh`), et l'écriture
   * attend cette lecture : si le serveur ne la rend plus, l'empreinte des réponses les écarte
   * (`funnel/answerSession.ts`), `answered` retombe à faux, et rien ne part.
   */
  useEffect(() => {
    if (mode !== "visitor") return;
    if (sent.current || !answered || loading || unavailable || revalidating) return;
    sent.current = true;
    registeredBefore.current = isRegistered(recorded);
    write();
    // `answers` comes from this history entry and does not change; the effect must
    // not re-run on the mutation's own state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answered, loading, unavailable, revalidating, mode]);

  /**
   * ═══ Y A-T-IL UNE ÉCRITURE À ATTENDRE ? ═══
   *
   * ⚠️ DES RÉPONSES EN MAIN NE SUFFISENT PAS : IL FAUT QUELQU'UN QUI ÉCRIVE. C'est la
   * condition de l'effet ci-dessus, relue ici plutôt que devinée — et c'est LE défaut,
   * pas un raffinement. L'attente se lisait sur `answered` seul, alors que la garde
   * `mode !== "visitor"` arrête l'écriture sans rien dire à l'écran : `wrote` ne quittait
   * jamais `"waiting"`, donc `working` restait vrai POUR TOUJOURS. L'organisateur qui
   * parcourt son tunnel — panneau ouvert, « Modifier mon inscription », puis Continuer
   * jusqu'ici — restait sur « Enregistrement de votre réponse », tourniquet compris, sans
   * qu'aucune requête ne parte (bug #154, mesuré dans le navigateur : zéro appel
   * d'écriture, aucune erreur en console). Tout ce que `settled` commande — le titre
   * configuré, le récapitulatif, le paiement, les factures, les actions — restait donc
   * invisible sur le seul écran qu'il venait régler.
   *
   * ⚠️ ET C'EST L'ATTENTE QU'ON CORRIGE, PAS L'ÉCRITURE : ne rien écrire pour
   * l'organisateur reste la règle, et c'est la raison d'être de la garde (l'inscrire à
   * l'événement qu'il prépare serait le vrai dégât). Un `setWrote("done")` de
   * consolation aurait dit « écrit » d'une écriture qui n'a pas eu lieu — et `wrote` sert
   * aussi à savoir si le badge existe, qui l'aurait cru prêt sur cette parole.
   *
   * ⚠️ L'ÉCHEC SUIT LA MÊME RÈGLE, et il avait le même défaut : sans écriture en vol, une
   * identité injoignable annonçait « votre réponse n'a pas pu être enregistrée » à
   * quelqu'un qui n'avait rien demandé d'enregistrer.
   *
   * Calculé AVANT tout rendu conditionnel, parce que la lecture du solde juste en dessous
   * en dépend et qu'un hook ne peut pas vivre après un `return`.
   */
  const recording = answered && mode === "visitor";
  const failed = recording && (unavailable || wrote === "failed");
  const working = recording && !failed && wrote !== "done";
  const settled = !working && !failed;

  /**
   * ═══ TROIS CAS, TROIS TEXTES ═══ refus, réglé, et RESTE À RÉGLER.
   *
   * ⚠️ CE TROISIÈME CAS MANQUAIT, ET IL MENTAIT. « Votre inscription est enregistrée » lu
   * par quelqu'un dont le solde reste dû lui fait croire que tout est en ordre : il
   * repart rassuré, et découvre le contraire à la porte.
   *
   * ⚠️ IL SE LIT SUR LE SOLDE, ET NON PLUS SUR `isCompleted`. Cette colonne semblait
   * toute désignée — c'est elle que le contrôle d'accès consulte — mais elle est écrite
   * par les deux fronts et par le paiement, et elle a dérivé : relevée en base, elle est
   * vraie pour 1 238 personnes de Serre-Chevalier 2026 qui n'ont jamais répondu. Le
   * solde, lui, est calculé à la demande par le serveur et ne peut pas mentir. Voir
   * `funnel/participant.ts`, qui dit pourquoi ce front n'y touche plus du tout.
   *
   * ⚠️ AUCUNE REQUÊTE DE PLUS : c'est la MÊME que celle du bloc de paiement juste en
   * dessous (même clé, même cache — voir `useResume`), et elle ne part que sur une
   * écriture aboutie. Sans billetterie elle répond « rien à payer », donc personne ne
   * voit ce texte, ce qui est le cas de l'immense majorité des événements.
   */
  const {
    resume,
    loading: resumeLoading,
    fetching: resumeFetching,
  } = useResume(null, settled);

  /**
   * ═══ L'EMAIL DE CONFIRMATION, UNE FOIS L'ÉCRITURE ABOUTIE ═══
   *
   * Quelles actions partent, et pourquoi à chaque modification : `emails/confirmation.ts`.
   * Jamais en mode organisateur (il n'a rien enregistré).
   *
   * ⚠️ PAS TANT QU'IL RESTE À PAYER, D'OÙ L'ATTENTE DU SOLDE — ET D'UN SOLDE RELU. Le
   * premier passage l'a mis en cache ; à la modification suivante, la réponse d'avant
   * reste servie pendant la relecture, et y lire « rien à payer » ferait partir la
   * confirmation d'une commande qui vient de créer une dette. D'où `fetching`.
   *
   * ⚠️ UNE FOIS PAR ENTRÉE D'HISTORIQUE, ET C'EST TOUT CE QUI RESTE DE L'ANCIEN `once`.
   * Un rechargement ou un retour arrière rouvre cette entrée avec ses réponses, donc
   * réécrit : sans la marque, chacun renverrait l'email d'une modification qui n'a pas eu
   * lieu (vécu, c'était la raison d'être de `once`). Un nouveau passage par le tunnel est
   * une autre entrée, donc un autre envoi. La marque vit dans `sessionStorage` parce
   * qu'elle doit survivre au rechargement, comme la clé de l'entrée (portée par
   * `history.state`).
   */
  const emailed = useRef(false);
  useEffect(() => {
    if (mode !== "visitor" || wrote !== "done") return;
    if (resumeLoading || resumeFetching) return;
    if (emailed.current || !event || typeof me?.id !== "number") return;
    if (resume?.toPay === true) return;
    /* ⚠️ UN AUTRE COMPTE A OUVERT UNE SESSION ENTRE-TEMPS (bug 8) : l'email partirait vers
       `me`, qui n'est plus celui du navigateur. */
    if (!sameSession(sessionKey)) return;
    emailed.current = true;
    const mark = `confirmation-emailed:${event.eventId}:${entry}`;
    try {
      if (sessionStorage.getItem(mark)) return;
      sessionStorage.setItem(mark, "1");
    } catch {
      /* stockage indisponible : seul le rechargement renverrait, on l'accepte */
    }
    sendConfirmation(event, me, answer, answers.stand?.email);
    // `answers`, `me` et `event` sont ceux de cette arrivée ; l'effet ne suit que
    // l'aboutissement de l'écriture et l'arrivée du solde.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wrote, resumeLoading, resumeFetching, resume?.toPay, mode]);

  /* ⚠️ LU AVANT LE RETOUR ANTICIPÉ qui suit : un hook après un `return` conditionnel
     change le nombre de hooks entre deux rendus (« Rendered fewer hooks than expected »)
     au rechargement direct sur cet écran. */
  const badgeShown = useFlagOr("FO_MEETING_V2_CONFIRMATION_BADGE_SHOW");

  if (lost && start) {
    // `replace`: the address must not stay in the history, or Back walks straight
    // back into the redirect.
    return <Navigate to={withContext(start.path, event?.token)} replace />;
  }

  const toPay = forced ? forced === "pending" : !declined && resume?.toPay === true;

  /**
   * ═══ "SEE MY BADGE" ═══ and it only appears if the badge REALLY exists.
   *
   * ⚠️ THE RULE IS NOT REWRITTEN HERE: it is `badgeState`, the same function the badge
   * screen uses. Two screens answering "do you have a badge" differently is the defect
   * the prototype's review found — confirmation, home and badge contradicted each other.
   * A button leading to a lock is not a button.
   *
   * ⚠️ THE ANSWER JUST WRITTEN COMES BEFORE WHAT THE SERVER HOLDS. The re-read of the
   * registration is invalidated by the write, so `recorded` still carries the state from
   * BEFORE for one round trip: read alone, it would make the button flicker — absent,
   * then present — right after registering.
   */
  const badgePage = pageOf("badge");
  const badgeReady =
    badgeState(
      forced
        ? { responded: true, answer, unpaid: toPay }
        : {
            ...(wrote === "done" || recorded
              ? { responded: wrote === "done" ? true : recorded?.responded }
              : {}),
            ...(answer ? { answer } : {}),
            ...(resume ? { unpaid: resume.toPay } : {}),
          },
    ) === "ready";
  /* ⚠️ ET LA PAGE DOIT ÊTRE OUVERTE : fermée par l'organisateur, le bouton mènerait à la
     page introuvable. Voir `closedKey`. */
  const seeBadge =
    badgeShown && badgeReady && !!badgePage && !isClosed(badgePage, value);
  const title = declined ? declineTitle : toPay ? pendingTitle : comingTitle;
  /* Les clés de ce qui s'affiche, pour l'édition sur place : celle qu'on modifie est
     celle de la version à l'écran. À distance, c'est la ligne propre au distanciel
     qu'on écrit, même tant qu'elle reprend le message général. */
  const titleKey = declined
    ? "FO_MEETING_V2_CONFIRMATION_DECLINE_TITLE"
    : toPay
      ? "FO_MEETING_V2_CONFIRMATION_PENDING_TITLE"
      : "FO_MEETING_V2_CONFIRMATION_TITLE";
  const messageKey = declined
    ? "FO_MEETING_V2_CONFIRMATION_DECLINE_MESSAGE"
    : toPay
      ? "FO_MEETING_V2_CONFIRMATION_PENDING_MESSAGE"
      : remote
        ? "FO_MEETING_V2_CONFIRMATION_MESSAGE_REMOTE"
        : "FO_MEETING_V2_CONFIRMATION_MESSAGE";
  // The remote line only replaces the general one when the organiser wrote it.
  const message = declined
    ? declineMessage
    : toPay
      ? pendingMessage
      : remote && remoteMessage.trim()
        ? remoteMessage
        : comingMessage;

  const sentBeforeFailure =
    partial?.days && partial.tickets
      ? t(
          "PARTIAL_WRITE_DAYS_AND_TICKETS",
          "Vos jours et vos billets ont bien été transmis à l'organisateur, mais le reste de votre inscription n'a pas pu être enregistré. Réessayez, et prévenez-le directement si cela persiste.",
        )
      : partial?.days
        ? t(
            "PARTIAL_WRITE_DAYS",
            "Vos jours ont bien été transmis à l'organisateur, mais le reste de votre inscription n'a pas pu être enregistré. Réessayez, et prévenez-le directement si cela persiste.",
          )
        : partial?.tickets
          ? t(
              "PARTIAL_WRITE_TICKETS",
              "Vos billets ont bien été transmis à l'organisateur, mais le reste de votre inscription n'a pas pu être enregistré. Réessayez, et prévenez-le directement si cela persiste.",
            )
          : t(
              "NOTHING_SENT_TO_ORGANISER",
              "Rien n'a été transmis à l'organisateur. Réessayez, et prévenez-le directement si cela persiste.",
            );

  /* Deux clics = deux écritures concurrentes (jours, commande, participation) :
     exactement la course que `saveParticipation` évite en série — `write` refuse la seconde. */
  const retry = write;

  return (
    <section className="page">
      <div className="confirm" data-cfg="confirmationTitle">
        {working && (
          <>
            <span className="spin" aria-hidden="true" />
            <h2>
              {t("SAVING_YOUR_ANSWER", "Enregistrement de votre réponse")}
            </h2>
            <p role="status">
              {t(
                "SAVING_YOUR_ANSWER_WAIT",
                "Un instant, nous la transmettons à l'organisateur.",
              )}
            </p>
          </>
        )}

        {failed && (
          <>
            <h2>
              {t(
                "ANSWER_NOT_SAVED",
                "Votre réponse n'a pas pu être enregistrée",
              )}
            </h2>
            {/* `role="alert"` so it is ANNOUNCED: somebody who cannot see the
                screen must not walk away believing it worked. */}
            <p role="alert">
              {sentBeforeFailure}
            </p>
          </>
        )}

        {settled && (
          <>
            <InPlace settingKey={titleKey} text={title} as="h2" />
            <InPlace settingKey={messageKey} text={message} as="p" multiline />
          </>
        )}
      </div>

      {/* Le bloc libre de l'organisateur, sous le titre : son HTML, assaini comme
          partout (`ui/Prose`). Une fois la réponse ÉCRITE seulement, comme le reste de
          l'écran : un mot de bienvenue à côté d'un échec serait le même mensonge que
          le récapitulatif. Vide, rien ne s'affiche. */}
      {settled && html.trim() !== "" && (
        <Prose html={html} className="se-prose" data-cfg="confirmationTitle" />
      )}

      {failed && (
        <div className="confcols">
          <Button type="button" onClick={retry}>
            {t("RETRY", "Réessayer")}
          </Button>
        </div>
      )}

      {/* ⚠️ ONLY ONCE THE WRITE HAS LANDED. A recap shown beside a failed write
          would list, line by line, a registration that does not exist — the most
          convincing possible version of the lie this screen exists to avoid. */}
      {settled && recapShown && (
        <div className="confcols">
          <Recap
            answers={forced ? { ...answers, participation: answer } : answers}
            recorded={recorded}
            me={me}
          />
        </div>
      )}

      {/**
       * ⚠️ APRÈS L'ÉCRITURE, ET C'EST UNE CONDITION DE JUSTESSE, PAS D'ORDRE VISUEL.
       * Le montant vient du serveur, qui le calcule sur ce qui est ENREGISTRÉ :
       * proposé avant que la commande soit écrite, il facturerait la précédente. La
       * garde `settled` est donc ce qui rend le montant vrai.
       *
       * ⚠️ ET LE COMPOSANT DÉCIDE SEUL DE SE TAIRE : il ne rend rien quand il n'y a
       * rien à régler — c'est-à-dire sur l'immense majorité des événements.
       */}
      {/**
       * ⚠️ IL NE DÉPEND PAS DE L'ÉTAPE BILLETTERIE, ET C'EST UN TROU QU'IL A FALLU
       * REFERMER. Le bloc était conditionné à « cette étape est du parcours », pour
       * économiser une requête sur les événements qui ne vendent rien. Mais une dette
       * n'a pas besoin de cette étape pour exister : une commande passée au back office,
       * une inscription faite sous la V1, une étape retirée depuis — dans tous ces cas
       * le participant devait de l'argent et n'avait AUCUN moyen de le régler, sans
       * qu'un mot le lui dise.
       *
       * Ce qui décide, c'est le solde, et lui seul : le composant se tait de lui-même
       * quand il n'y a rien à payer.
       */}
      {settled && (
        <Checkout active={payShown} example={forced === "pending"} />
      )}

      {/* Les factures viennent APRÈS le paiement, dans l'ordre où les choses se font :
          on règle, puis on récupère son document. Le bloc se tait quand il n'y en a
          aucune, ce qui est le cas de presque tout le monde. */}
      {/* Même raison : une facture réglée existe indépendamment du parcours qu'on sert
          aujourd'hui. La liste se tait quand il n'y en a aucune. */}
      {settled && <InvoiceList active={invoicesShown} />}

      {/* ⚠️ ONE PRIMARY, THE LAST — the prototype's rule, and the reason its actions
          read as a decision rather than a menu. Two identical ghost buttons offer no
          hierarchy: nothing says which one somebody is expected to press.

          ⚠️ AND THE BADGE TAKES THAT PLACE WHEN IT EXISTS. Somebody who has just
          registered has an obvious next move — seeing their badge — and going back home
          is not it. The way home stays on offer, in second place.

          ⚠️ « MODIFIER » APRÈS « RETOUR À L'ACCUEIL », et non l'inverse (demandé le
          2026-08-05). Les trois gestes vont du plus doux au plus engageant en allant
          vers la droite, et refaire son inscription est ce qu'on offre en DERNIER
          recours, pas ce qu'on propose en premier — la rangée se lisait comme une
          invitation à recommencer. */}
      {settled && (editShown || backShown || seeBadge) && (
        <div className="actions">
          {backShown && (
            <Button
              type="button"
              variant={seeBadge ? "ghost" : undefined}
              data-cfg="confirmationBack"
              onClick={() => navigate(withContext("/", event?.token))}
            >
              {backLabel}
            </Button>
          )}
          {editShown && (
            <Button
              type="button"
              variant="ghost"
              data-cfg="confirmationEdit"
              onClick={() =>
                navigate(withContext("/registration", event?.token))
              }
            >
              {t("EDIT_MY_REGISTRATION", "Modifier mon inscription")}
            </Button>
          )}
          {seeBadge && badgePage && (
            <Button
              type="button"
              data-cfg="confirmationBadge"
              onClick={() =>
                navigate(withContext(pagePath(badgePage), event?.token))
              }
            >
              {t("SEE_MY_BADGE", "Voir mon badge")}
            </Button>
          )}
        </div>
      )}
    </section>
  );
}
